import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@core-ui/Components/ui/dialog';
import { Button } from '@core-ui/Components/ui/button';
import { Label } from '@core-ui/Components/ui/label';
import { Textarea } from '@core-ui/Components/ui/textarea';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectValue,
} from '@core-ui/Components/ui/select';
import { cn } from '@core-ui-lib/utils.ts';
import FuegoLead from '@ui/shared/Dialer/FuegoLead.tsx';
import { useWizardStore } from '@features/wizard';

interface Option { value: string; label: string }

interface Props {
  id?: string;
  name?: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  options: Option[];
  value: string;
  onChange: (v: string) => void;
  agentNotes?: string;
  onNotesChange: (v: string) => void;
  showAgentNotes?: boolean;
  showSdrNotes?: boolean;
  sdrNotes?: string;
  onSdrNotesChange?: (v: string) => void;
  showSpecify?: boolean;
  submitDisabled?: boolean;
  fuegoLead?: boolean;
  onFuegoLeadChange?: (value: boolean) => void;
  onSubmit: () => void;
  showFuegoLead: boolean;
}

const DispositionsModal: React.FC<Props> = ({
  id,
  name,
  open,
  options,
  value,
  onChange,
  agentNotes,
  onNotesChange,
  showAgentNotes = true,
  showSpecify = false,
  showSdrNotes = false,
  sdrNotes = '',
  onSdrNotesChange,
  submitDisabled = false,
  fuegoLead,
  onFuegoLeadChange,
  onSubmit,
  showFuegoLead,
}) => {
  const { t } = useTranslation();

  const preventClose = (e: Event): void => {
    e.preventDefault();
    e.stopPropagation();
  };

  const remaining = (max: number, text?: string): number =>
    Math.max(0, max - (text?.length ?? 0));

  const agentNotesRef = React.useRef<HTMLTextAreaElement | null>(null);
  const sdrNotesRef = React.useRef<HTMLTextAreaElement | null>(null);
  const { setIsSessionStarted } = useWizardStore();
  
  React.useEffect(() => {
    if (open) {
      if (agentNotesRef.current != null) {
        agentNotesRef.current.scrollTop = agentNotesRef.current.scrollHeight;
      }
      if (sdrNotesRef.current != null) {
        sdrNotesRef.current.scrollTop = sdrNotesRef.current.scrollHeight;
      }
    }
  }, [open]);

  const handleSubmit = (): void => {
    setIsSessionStarted(false);
    open = false;
    onSubmit();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={() => {
        // Modal is non-dismissable — SDR must submit a disposition
      }}
    >
      <DialogContent
        className="rounded-2xl shadow-2xl sm:max-w-lg bg-card text-foreground border border-border [&_div[data-radix-dialog-overlay]]:bg-black/60 [&_div[data-radix-dialog-overlay]]:backdrop-blur-sm [&>button:last-child]:hidden"
        onInteractOutside={preventClose}
        onEscapeKeyDown={preventClose}
        onPointerDownOutside={preventClose}
      >
        <DialogHeader>
          <DialogTitle className="text-foreground">
            {t('dialer.dispositions.title', 'Dispositions')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Select principal */}
          <div>
            <Label className="mb-2 block text-foreground">
              {t('dialer.dispositions.selectLabel', 'Please Select a Call Disposition')}
            </Label>
            <Select value={value} onValueChange={onChange}>
              <SelectTrigger className="w-full bg-background text-foreground border-border">
                <SelectValue
                  placeholder={t('dialer.dispositions.selectPlaceholder', 'Select a disposition…')}
                />
              </SelectTrigger>
              <SelectContent className="bg-popover text-popover-foreground border border-border">
                <SelectGroup>
                  {options.map((opt) => (
                    <SelectItem
                      key={opt.value}
                      value={opt.value}
                      className="text-popover-foreground focus:bg-accent focus:text-accent-foreground"
                    >
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          {/* Notas SDR */}
          {showSdrNotes && (
            <div className="space-y-2">
              <Label className="block text-foreground">
                {(onSdrNotesChange != null)
                  ? t('dialer.dispositions.sdrNotes', 'SDR Notes')
                  : t('dialer.dispositions.sdrNotesReadonly', 'SDR Notes')}
              </Label>

              {(onSdrNotesChange != null) ? (
                <>
                  <Textarea
                    ref={sdrNotesRef}
                    value={sdrNotes}
                    maxLength={300}
                    rows={5}
                    placeholder={t('dialer.dispositions.sdrNotesPlaceholder', 'Write SDR notes here…')}
                    onChange={(e) => {
                      const next = e.target.value;
                      if (next.length >= (sdrNotes ?? '').length) {
                        onSdrNotesChange(next);
                      } else {
                        onSdrNotesChange(next);
                      }
                    }}
                    className="text-foreground placeholder:text-muted-foreground bg-background border border-border break-all"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {remaining(300, sdrNotes)} {t('dialer.dispositions.charactersLeft', 'characters left')}
                  </p>
                </>
              ) : (
                <div className="border border-border rounded-md bg-muted/30 p-2 text-sm text-foreground whitespace-pre-wrap min-h-[52px]">
                  {(sdrNotes?.trim()?.length ?? 0) > 0
                    ? sdrNotes
                    : t('dialer.dispositions.noSdrNotes', 'No SDR notes.')}
                </div>
              )}
            </div>
          )}

          {showAgentNotes && (
            <div>
              <Label className="mb-2 block text-foreground">
                {t('dialer.dispositions.agentNotes', 'Agent Notes (Optional)')}
              </Label>
              <Textarea
                ref={agentNotesRef}
                value={String(agentNotes)}
                maxLength={300}
                rows={5}
                placeholder={t('dialer.dispositions.agentNotesPlaceholder', 'Write agent notes here…')}
                onChange={(e) => {
                  const next = e.target.value;
                  if (next.length >= String(agentNotes).length) {
                    onNotesChange(next);
                  } else {
                    onNotesChange(next);
                  }
                }}
                className="text-foreground placeholder:text-muted-foreground bg-background border border-border break-all"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {remaining(300, agentNotes)} {t('dialer.dispositions.charactersLeft', 'characters left')}
              </p>
            </div>
          )}

          {showSpecify && (
            <div>
              <Label className="mb-2 block text-foreground">
                {t('dialer.dispositions.specify', 'Please specify')}
              </Label>
              <Textarea
                value={String(agentNotes)}
                maxLength={200}
                rows={4}
                onChange={(e) => {
                  onNotesChange(e.target.value);
                }}
                className="text-foreground placeholder:text-muted-foreground bg-background border border-border break-all"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {remaining(200, agentNotes)} {t('dialer.dispositions.charactersLeft', 'characters left')}
              </p>
            </div>
          )}

          <div className={cn('flex gap-2 pt-2',
            showFuegoLead ? 'justify-between' : 'justify-end')}>
            {showFuegoLead && (
              <FuegoLead
                contactId={id ?? ''}
                contactName={name ?? ''}
                fuegoLead={fuegoLead ?? false}
                onFuegoLeadChange={onFuegoLeadChange}
              />
            )}
            <Button onClick={handleSubmit} disabled={(value === '') || submitDisabled}>
              {t('dialer.dispositions.common.submit', 'Submit')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DispositionsModal;
